import { Box } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { StyleObj } from '../../@types';
import { CasinoSummaryQueryParams, CasinoSummaryRow } from '../../@types/casino';
import {
  appendCasinoTotalsRow,
  buildCasinoSummaryTotalsRow,
  getDefaultCasinoReportRole,
  getCasinoTotalsRowClass,
  prepareCasinoReportFilters,
} from '../../helpers/casino';
import { createNumberColumn } from '../../helpers/columns';
import { useCasinoTableQuery } from '../../hooks/useCasinoTableQuery';
import { useMarketplace } from '../../hooks/useMarketplace';
import usePermissions from '../../hooks/usePermissions';
import { useCasinoSummary } from '../../queries/casino';
import { createColumn } from '../../utils';
import DateRangeSelect, { DayjsTimestamp } from '../molecules/DateRangeSelect';
import CasinoReportsStatistics from '../organisms/CasinoReportsStatistics';
import TableTemplate from '../templates/TableTemplate';

const DEFAULT_BETTING_ACTIVITY = 'ACTIVE';

const createDefaultDateRange = (): DayjsTimestamp => ({
  fromTimestamp: dayjs().startOf('day'),
  toTimestamp: dayjs().endOf('day'),
});

const createDefaultSummaryQueryParams = (date: DayjsTimestamp, role: string): CasinoSummaryQueryParams => ({
  page: 1,
  limit: 25,
  dateFrom: date.fromTimestamp.valueOf(),
  dateTo: date.toTimestamp.valueOf(),
  role,
  bettingActivity: DEFAULT_BETTING_ACTIVITY,
});

const EMPTY_SUMMARY_FIELDS: Array<Extract<keyof CasinoSummaryQueryParams, string>> = [
  'entityId',
  'providerName',
  'gameName',
];

const EMPTY_SUMMARY_ARRAY_FIELDS: Array<Extract<keyof CasinoSummaryQueryParams, string>> = [
  'managerIds',
  'agentIds',
  'playerIds',
];

const styles: StyleObj = {
  container: {
    position: 'relative',
    height: 'calc(100% - 200px)',
  },
};

const DEFAULT_SORT_COLUMN = 'user';
const DEFAULT_SORT_ORDER = 'asc';

const CasinoReportsPage = () => {
  const { role: userRole } = usePermissions();
  const { config } = useMarketplace();
  const isJackpotEnabled = config?.casinoConfig?.jackpotEnabled === true;
  const [date, setDate] = useState<DayjsTimestamp>(createDefaultDateRange);
  const defaultReportRole = getDefaultCasinoReportRole(userRole);
  const { queryParams, paginationModel, updateQueryParams, handleSort, changeQuery, changeDateRange } =
    useCasinoTableQuery<CasinoSummaryQueryParams>({
      defaultQuery: () => createDefaultSummaryQueryParams(date, defaultReportRole),
      emptyFields: EMPTY_SUMMARY_FIELDS,
      emptyArrayFields: EMPTY_SUMMARY_ARRAY_FIELDS,
      prepareQuery: prepareCasinoReportFilters,
      sortDefaults: {
        sortColumn: 'sortColumn',
        sortOrder: 'sortOrder',
        defaultColumn: DEFAULT_SORT_COLUMN,
        defaultOrder: DEFAULT_SORT_ORDER,
      },
    });

  const {
    data: summaryData,
    isFetching: isSummaryFetching,
    isLoading: isSummaryLoading,
  } = useCasinoSummary(queryParams);

  const columns = useMemo<GridColDef<CasinoSummaryRow>[]>(
    () => [
      createColumn('user', 'Name', {
        minWidth: 220,
        flex: 1,
        valueGetter: (_value, row, _column, _apiRef) => row.user?.username ?? '--',
      }),
      // Not sortable: casino-api merges both in from the wallet service after it paginates the
      // report, so it cannot order by them.
      createNumberColumn('totalDistributedBonusAmount', 'Distributed Bonus', { sortable: false }),
      ...(isJackpotEnabled ? [createNumberColumn('jackpotAmount', 'Jackpots', { sortable: false })] : []),
      createNumberColumn('totalPaidInAmount', 'Total Paid In'),
      createNumberColumn('totalPaidOutAmount', 'Total Paid Out'),
      createNumberColumn('totalProfit', 'Gross Summary'),
      createNumberColumn('ngr', 'NGR', {
        sortable: false,
      }),
    ],
    [isJackpotEnabled]
  );

  const handleDateChange = useCallback(
    (newDate: DayjsTimestamp) => {
      setDate(newDate);
      changeDateRange({
        dateFrom: newDate.fromTimestamp.valueOf(),
        dateTo: newDate.toTimestamp.valueOf(),
      });
    },
    [changeDateRange]
  );

  const rows = appendCasinoTotalsRow(summaryData?.items ?? [], buildCasinoSummaryTotalsRow(summaryData?.totals));

  return (
    <Box sx={styles.container}>
      <CasinoReportsStatistics />

      <TableTemplate
        rows={rows}
        columns={columns}
        loading={isSummaryFetching || isSummaryLoading}
        rowCount={summaryData?.count || 0}
        handlePaginationModelChange={updateQueryParams}
        handleSort={handleSort}
        changeQuery={changeQuery}
        getRowClassName={getCasinoTotalsRowClass}
        paginationModel={paginationModel}
        filterType='casino-reports'
        customTableHeaderContent={<DateRangeSelect date={date} onDateChange={handleDateChange} />}
        showFilter
      />
    </Box>
  );
};

export default CasinoReportsPage;
